// OpenAI Adapter for Unified LLM Interface

/**
 * Convert unified messages to OpenAI format
 */
const convertMessagesToOpenAI = (messages) => {
  return messages.map((msg) => {
    if (msg.role === 'system' || msg.role === 'user' || msg.role === 'assistant') {
      return { role: msg.role, content: msg.content }
    }
    if (msg.role === 'tool') {
      return { role: 'tool', tool_call_id: msg.toolCallId, content: msg.content }
    }
    return msg
  })
}

/**
 * Create OpenAI adapter
 * @param {Object} openaiClient - OpenAI client instance
 * @returns {Object} Adapter with chat and listModels methods
 */
export function createOpenaiAdapter(openaiClient) {
  const chat = async ({ apiKey, model = 'gpt-4', messages, tools, config = {} }) => {
    const openaiMessages = convertMessagesToOpenAI(messages)

    const openaiTools = tools
      ? tools.map((tool) => ({
          type: 'function',
          function: {
            name: tool.name,
            description: tool.description,
            parameters: tool.parameters,
          },
        }))
      : undefined

    const response = await openaiClient.chatCompletions({
      apiKey,
      model,
      messages: openaiMessages,
      tools: openaiTools,
      tool_choice: config.toolChoice || 'auto',
      temperature: config.temperature,
      max_tokens: config.maxTokens,
      top_p: config.topP,
      frequency_penalty: config.frequencyPenalty,
      presence_penalty: config.presencePenalty,
    })

    const message = response.choices[0].message

    const result = {
      content: message.content,
      role: 'assistant',
      finishReason: response.choices[0].finish_reason,
      usage: {
        promptTokens: response.usage?.prompt_tokens,
        completionTokens: response.usage?.completion_tokens,
        totalTokens: response.usage?.total_tokens,
      },
    }

    if (message.tool_calls && message.tool_calls.length > 0) {
      result.toolCalls = message.tool_calls.map((tc) => ({
        id: tc.id,
        type: 'function',
        function: { name: tc.function.name, arguments: tc.function.arguments },
      }))
    }

    return { message: result, raw: response }
  }

  const listModels = async ({ apiKey }) => {
    return await openaiClient.listModels({ apiKey })
  }

  return { chat, listModels }
}
